import { useParams, Link, useNavigate } from 'react-router-dom';
import { MapPin, Calendar, Building, ArrowLeft, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useState, useEffect } from 'react';
import Breadcrumb from '@/components/Breadcrumb';
import Lightbox from '@/components/Lightbox';

const ProjectDetail = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    fetchProject();
  }, [projectId]);

  // Keyboard navigation for image gallery
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!project?.images || project.images.length <= 1) return;
      
      if (e.key === 'ArrowLeft') {
        setSelectedImage(prev => prev === 0 ? project.images.length - 1 : prev - 1);
      } else if (e.key === 'ArrowRight') {
        setSelectedImage(prev => prev === project.images.length - 1 ? 0 : prev + 1);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [project, selectedImage]);

  const fetchProject = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Tüm projeleri çek
      const response = await fetch('/api/projects-public');
      
      if (!response.ok) {
        throw new Error('Projeler yüklenirken bir hata oluştu');
      }
      
      const projects = await response.json();
      
      // ID'ye göre projeyi bul
      const foundProject = projects.find(p => p.id === projectId);
      
      if (!foundProject) {
        setError('Proje bulunamadı');
      } else {
        setProject(foundProject);
      }
    } catch (error) {
      console.error('Fetch error:', error);
      setError('Proje yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // Yükleme durumu
  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="text-xl text-gray-600">Yükleniyor...</div>
      </div>
    );
  }

  // Hata durumu
  if (error || !project) {
    return (
      <div className="min-h-screen pt-24 pb-16 flex items-center justify-center">
        <div className="text-center">
          <h1 className="heading-font text-4xl font-bold text-[#1e4a7a] mb-4">
            {error || 'Proje Bulunamadı'}
          </h1>
          <Button 
            onClick={() => navigate('/projelerimiz')}
            className="bg-[#dc2626] hover:bg-[#b91c1c]"
          >
            Projelere Dön
          </Button>
        </div>
      </div>
    );
  }

  // Specs oluştur
  const specs = project.specs || [
    { label: 'Toplam Kat', value: project.totalFloors || 'Belirtilmemiş' },
    { label: 'Bağımsız Bölüm', value: project.units || 'Belirtilmemiş' },
    { label: 'Proje Tipi', value: project.projectType || 'Belirtilmemiş' }
  ];

  return (
    <div data-testid="project-detail-page" className="min-h-screen pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <Breadcrumb 
          items={[
            { label: 'Projelerimiz', path: '/projelerimiz' },
            { label: project.name }
          ]} 
        />
        
        {/* Back Button */}
        <button
          onClick={() => navigate('/projelerimiz')}
          data-testid="back-button"
          className="flex items-center text-gray-600 hover:text-[#dc2626] mb-8 transition-colors"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          Projelere Geri Dön
        </button>

        {/* Header */}
        <div className="bg-gradient-to-r from-[#1e4a7a] to-[#2563eb] rounded-3xl p-8 sm:p-12 text-white mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className={`px-4 py-1 rounded-full text-sm font-medium ${
              project.status === 'Devam Ediyor' 
                ? 'bg-blue-500' 
                : project.status === 'Planlama Aşamasında'
                ? 'bg-yellow-500'
                : 'bg-green-500'
            }`}>
              {project.status}
            </div>
            <div className="px-4 py-1 bg-white/20 rounded-full text-sm font-medium">
              {project.category}
            </div>
          </div>
          <h1 className="heading-font text-4xl sm:text-5xl font-bold mb-4">{project.name}</h1>
          <div className="space-y-2">
            <div className="flex items-start gap-2">
              <MapPin className="h-5 w-5 mt-1 flex-shrink-0" />
              <span className="text-lg">{project.fullLocation || project.location}</span>
            </div>
            <div className="flex items-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>{project.year}</span>
              </div>
              <div className="flex items-center gap-2">
                <Building className="h-4 w-4" />
                <span>{project.location}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Image Gallery */}
        {project.images && project.images.length > 0 && (
          <div className="mb-12">
            <div className="mb-4 relative group cursor-pointer" onClick={() => setLightboxOpen(true)}>
              <img
                src={project.images[selectedImage]}
                alt={project.name}
                className="w-full h-[500px] object-cover rounded-2xl shadow-xl hover:shadow-2xl transition-shadow"
              />
              
              {/* Click to Enlarge Overlay */}
              <div className="absolute inset-0 bg-black/0 hover:bg-black/20 transition-all duration-300 rounded-2xl flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 px-6 py-3 rounded-full text-gray-800 font-medium">
                  🔍 Büyütmek için tıklayın
                </div>
              </div>
              
              {/* Navigation Buttons */}
              {project.images.length > 1 && (
                <>
                  {/* Previous Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedImage(selectedImage === 0 ? project.images.length - 1 : selectedImage - 1);
                    }}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 rounded-full p-3 shadow-lg opacity-0 group-hover:opacity-100 transition-all duration-300 hover:scale-110"
                    aria-label="Önceki Fotoğraf"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  
                  {/* Next Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedImage(selectedImage === project.images.length - 1 ? 0 : selectedImage + 1);
                    }}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 rounded-full p-3 shadow-lg opacity-0 group-hover:opacity-100 transition-all duration-300 hover:scale-110"
                    aria-label="Sonraki Fotoğraf"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                  
                  {/* Image Counter */}
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 text-white px-4 py-2 rounded-full text-sm font-medium backdrop-blur-sm">
                    📷 {selectedImage + 1} / {project.images.length}
                  </div>
                </>
              )}
            </div>
            
            {/* Thumbnail Grid */}
            {project.images.length > 1 && (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-4">
                {project.images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`relative rounded-lg overflow-hidden border-2 transition-all hover:scale-105 ${
                      selectedImage === index ? 'border-[#dc2626] ring-2 ring-[#dc2626]/50 scale-105' : 'border-gray-200 hover:border-gray-400'
                    }`}
                  >
                    <img
                      src={image}
                      alt={`${project.name} ${index + 1}`}
                      className="w-full h-20 object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Video Section - if available */}
        {project.videoUrl && (
          <div className="mb-12">
            <h2 className="heading-font text-3xl font-bold text-[#1e4a7a] mb-6">Proje Videosu</h2>
            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
              <iframe
                src={project.videoUrl}
                title={`${project.name} Video`}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="absolute top-0 left-0 w-full h-full rounded-2xl shadow-xl"
              ></iframe>
            </div>
          </div>
        )}

        {/* Content Grid */}
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Description */}
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h2 className="heading-font text-3xl font-bold text-[#1e4a7a] mb-4">Proje Detayları</h2>
              <p className="text-gray-600 leading-relaxed text-lg">{project.description}</p>
            </div>

            {/* Features */}
            {project.features && project.features.length > 0 && (
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <h2 className="heading-font text-3xl font-bold text-[#1e4a7a] mb-6">Yapı Özellikleri</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {project.features.map((feature, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl"
                    >
                      <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700 font-medium">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Specs */}
            <div className="bg-white rounded-2xl p-6 shadow-lg sticky top-24">
              <h3 className="heading-font text-2xl font-bold text-[#1e4a7a] mb-6">Teknik Bilgiler</h3>
              <div className="space-y-4">
                {specs.map((spec, index) => (
                  <div key={index} className="border-b border-gray-100 pb-4 last:border-0">
                    <div className="text-sm text-gray-500 mb-1">{spec.label}</div>
                    <div className="heading-font text-xl font-semibold text-[#1e4a7a]">{spec.value}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Lightbox */}
      {lightboxOpen && (
        <Lightbox
          images={project.images}
          initialIndex={selectedImage}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </div>
  );
};

export default ProjectDetail;